import { createStyles } from '@mantine/core';
import type { PropsWithChildren } from 'react';
import { useContext } from 'react';

import { SplashLoader } from '~/domains/common/components/SplashLoader/SplashLoader';

import { useContestContext } from '../../context/ContestContext/ContestContext';
import { GamecenterContestFormatNotSupported } from '../GamecenterContestFormatNotSupported/ContestFormatNotSupported';
import GamecenterHeader from '../GamecenterHeader/GamecenterHeader';
import GamecenterNonScheduledNav from '../GamecenterNonScheduledLayout/GamecenterNonScheduledNav/GamecenterNonScheduledNav';
import GamecenterPrevPathContext from '../../context/GamecenterPrevPathContext/GamecenterPrevPathContext';
import type { TScreen } from '../../hooks/useIsGamecenterScreenSupportedOnNativeAppsOnly';
import useIsGamecenterScreenSupportedOnNativeAppsOnly from '../../hooks/useIsGamecenterScreenSupportedOnNativeAppsOnly';
import GamecenterPicksHeader from '../GamecenterPicksLayout/GamecenterPicksHeader/GamecenterPicksHeader';
import useGamecenterPicksHeader from '../GamecenterPicksLayout/GamecenterPicksHeader/useGamecenterPicksHeader';

const useStyles = createStyles((theme) => ({
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    zIndex: theme.other.zIndex.header,
  },
  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

function GamecenterSupportedMobileOnlyCheck({
  children,
  screen,
}: PropsWithChildren<{ screen: TScreen }>) {
  const { contest } = useContestContext();
  const headerProps = useGamecenterPicksHeader({
    expectedPicksCount: 0,
    currentUserEntry: undefined, // No entry context in this component
  });
  const { classes } = useStyles();
  const { prevLocationBeforeGamecenter } = useContext(GamecenterPrevPathContext);

  const { isReady, isSupported: isScreenSupportedOnNativeAppsOnly } =
    useIsGamecenterScreenSupportedOnNativeAppsOnly({
      contest,
      screens: [screen],
    });

  const { isSupported: isAllScreensSupportedOnNativeAppsOnly } =
    useIsGamecenterScreenSupportedOnNativeAppsOnly({
      contest,
    });

  if (!isReady) {
    return <SplashLoader />;
  }

  if (isScreenSupportedOnNativeAppsOnly) {
    return (
      <div className={classes.container}>
        <div className={classes.header}>
          {screen === 'picksheet' ? (
            <GamecenterPicksHeader {...headerProps} />
          ) : (
            <>
              <GamecenterHeader closeHref={prevLocationBeforeGamecenter} contest={contest} />
              {contest.is_after_first_late_swap_deadline &&
              !isAllScreensSupportedOnNativeAppsOnly ? (
                <GamecenterNonScheduledNav contest={contest} />
              ) : null}
            </>
          )}
        </div>
        <div className={classes.content}>
          <GamecenterContestFormatNotSupported contestId={contest.id} />
        </div>
      </div>
    );
  }

  return children;
}

export default GamecenterSupportedMobileOnlyCheck;
